<template>
  <article>
    <AppBanner
      title="Devices"
      title-alignment="text-left flex-row max-w-[1600px]"
    >
      <AppButton
        type="button"
        class="btn-solid btn-secondary rounded-sm px-3 md:px-5 whitespace-nowrap"
        @click="showAddDeviceModal = true"
      >
        <FontAwesomeIcon :icon="faPlus" />
        Add New
      </AppButton>
    </AppBanner>

    <section class="max-w-[1600px] mx-auto container-padding flex flex-col gap-2 my-8">
      <AppTable
        :loading="loading"
        :if-looped-items-exist="devices?.length > 0"
        class="device-table max-w-[1600px] mx-auto"
      >
        <template #headings>
          <th>Name</th>
          <th>Linked Event</th>
          <th class="sr-only" />
        </template>

        <template #rows>
          <tr
            v-for="device in devices"
            :key="device.name"
          >
            <td>{{ device?.name }}</td>
            <td>{{ device?.event?.name || '-' }}</td>
            <td>
              <div class="table-dropdown">
                <AppDropdown class="admin-table-dropdown">
                  <AppDropdownItem @click="deviceToUnlink = device">
                    Un-link
                  </AppDropdownItem>

                  <AppDropdownItem @click="deviceToEdit = device">
                    Edit
                  </AppDropdownItem>

                  <AppDropdownItem @click="deviceToDelete = device">
                    Delete
                  </AppDropdownItem>
                </AppDropdown>
              </div>
            </td>
          </tr>
        </template>
      </AppTable>
    </section>

    <Transition name="fade">
      <ConfirmationModal
        v-if="deviceToUnlink"
        title="Unlink Device"
        :processing="isSubmitting"
        class="unlink-device-modal"
        size="xl"
        @confirm="handleEditDevice({ name:deviceToUnlink.name, event:null })"
        @close="deviceToUnlink = null"
      >
        <span class="inline-block mb-12 text-lg">
          Are you sure you want to un-link

          <span class="font-semibold">{{ deviceToUnlink.name }}</span>
          from this event?
        </span>
      </ConfirmationModal>
    </Transition>

    <Transition name="fade">
      <DeleteModal
        v-if="deviceToDelete"
        title="Device"
        :error="error"
        :processing="isDeleting"
        @delete="handleDeleteDevice(deviceToDelete.id)"
        @close="deviceToDelete = null"
      >
        <span class="flex items-center gap-1 justify-center mb-12 text-lg">
          Are you sure you want to delete

          <span class="flex items-center">
            <span class="font-semibold">{{ deviceToDelete.name }}</span>
            ?
          </span>
        </span>
      </DeleteModal>
    </Transition>

    <!-- Edit Device -->
    <Transition name="fade">
      <AppModal
        v-if="deviceToEdit"
        :disabled="isSubmitting"
        class="event-overview-edit-modal"
        @close="() => { deviceToEdit = null; errors = null }"
      >
        <DeviceForm
          :device="deviceToEdit"
          :errors="errors"
          :processing="isSubmitting"
          @confirm="handleEditDevice"
          @close="() => { deviceToEdit = null; errors = null }"
        />
      </AppModal>
    </Transition>

    <!--Add device-->
    <Transition name="fade">
      <AppModal
        v-if="showAddDeviceModal"
        :disabled="isSubmitting"
        size="xl"
        class="event-overview-add-modal"
        @close="() => { showAddDeviceModal = false; errors = null }"
      >
        <DeviceForm
          :errors="errors"
          :processing="isSubmitting"
          @confirm="handleAddDevice"
          @close="() => { showAddDeviceModal = false; errors = null }"
        />
      </AppModal>
    </Transition>
  </article>
</template>

<script setup>
import { ref } from 'vue';
import {
  AppDropdown,
  AppDropdownItem,
  AppBanner,
  AppModal,
} from '@/components/app';

import { AppButton } from '@/components/app';

import { DeviceForm } from '@/components/forms/admin';

import { useHttp, useFormSubmit } from '@/composables';
import { HttpStatusCode } from 'axios';

import {
  DeleteModal,
  ConfirmationModal,
} from '@/components/modals';

import { AppTable } from '@/components/app';
import { TableNoResults } from '@/components/tables';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
const { isSubmitting, errors, error } = useFormSubmit();

const devices = ref([]);
const loading = ref(false);

const showAddDeviceModal = ref(false);

const deviceToUnlink = ref(null);
const deviceToDelete = ref(null);
const deviceToEdit = ref(null);

const isDeleting = ref(false);

const loadDevices = async () => {
  loading.value = true;

  const response = await useHttp().get('/devices');

  if (response.statusCode === HttpStatusCode.Ok) {
    devices.value = response.data.items;
  } else {
    errors.value = response.errors;
  }

  loading.value = false;
};

const handleDeleteDevice = async (id) => {
  isDeleting.value = true;

  const response = await useHttp().delete(`/devices/${id}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    await loadDevices();
    deviceToDelete.value = null;
  } else {
    error.value = 'Something went wrong. Please try again';
  }

  isDeleting.value = false;
};

const handleAddDevice = async (formData) => {
  errors.value = null;
  isSubmitting.value = true;

  const response = await useHttp().post('/devices', formData);

  if (response.statusCode === HttpStatusCode.Ok) {
    await loadDevices();
    showAddDeviceModal.value = false;
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    errors.value = response.errors;
  }

  isSubmitting.value = false;
};
const handleEditDevice = async (formData) => {
  errors.value = null;
  isSubmitting.value = true;

  const entity = deviceToEdit.value ? deviceToEdit.value : deviceToUnlink.value;

  const response = await useHttp().put(`/devices/${entity.id}`, formData);

  if (response.statusCode === HttpStatusCode.Ok) {
    await loadDevices();

    deviceToEdit.value = null;
    deviceToUnlink.value = null;

  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    errors.value = response.errors;
  }

  isSubmitting.value = false;
};

loadDevices();
</script>
